import { PositionController } from './PositionController';
import SceneManager, { SLIDE_TRANSITION_DURATION } from '../logic/SceneManager';
import { View } from '@play-co/timestep-core/ui';
import { fromEntityId, PetEntity } from '../logic/EntityManager';
import StateObserver from '../../StateObserver';
import { createWaiterInView, getScreen, getScreenTop } from '../../lib/utils';
import { Position, wanderLocations } from './PositionModel';
import { getPetAnimation } from '../appearance/appearance.config';
import type { PetController } from '../pet/PetController';
import { EntityPosition } from './position.config';

export class PetPositionController extends PositionController<PetEntity> {
  private static positions: Record<string, Position> = {};
  private wanderWaiter = createWaiterInView(this.view);
  private wanderingEnabled = true;
  private originalOffset: { x: number; y: number } = { x: 0, y: 0 };
  private entity: PetEntity;

  constructor(
    private pet: PetController,
    sceneManager: SceneManager,
  ) {
    super(sceneManager, pet.entity);

    this.entity = pet.entity;
    this.originalOffset = {
      x: this.entity.style.offsetX,
      y: this.entity.style.offsetY,
    };
  }

  public setWandering(isWandering: boolean) {
    if (!isWandering || !this.wanderingEnabled) {
      this.wanderWaiter.clear();
      return;
    }

    void this.wander();
  }

  public setWanderingEnabled(isEnabled: boolean) {
    this.wanderingEnabled = isEnabled;
    this.setWandering(isEnabled);
  }

  private async wander() {
    this.wanderWaiter.wait(() => this.wander(), 3000);

    const { care } = StateObserver.getState();
    if (!this.position) {
      return;
    }

    if (care.currentCareAction || this.pet.conversation.inProgress()) {
      return;
    }

    const { scene } = this.position;
    if (
      scene === 'livingRoom' ||
      scene === 'friendsLivingRoom' ||
      scene === 'arena'
    ) {
      let newLocation: Position['location'];
      do {
        const randomLocationIndex = Math.floor(
          Math.random() * wanderLocations.length,
        );
        newLocation = wanderLocations[randomLocationIndex];
      } while (
        // If there is already a pet in the new location, try again.
        Object.values(PetPositionController.positions).some(
          (position) => position?.location === newLocation,
        )
      );

      await this.moveTo({
        scene,
        location: newLocation,
      });
    }
  }

  protected getTransitionDuration(scene: View, destination: EntityPosition) {
    const superview = this.view.getSuperview();
    const { ui } = StateObserver.getState();
    if (scene === superview) {
      if (ui.isPetCrateOpening) {
        return 0;
      }
    } else if (superview !== null) {
      return SLIDE_TRANSITION_DURATION();
    }

    return super.getTransitionDuration(scene, destination);
  }

  protected prepareForTransition(sceneView: View) {
    const superview = this.view.getSuperview();
    if (superview === null) {
      super.prepareForTransition(sceneView);
      return;
    }

    if (sceneView === superview) return;

    this.view.updateOpts({ superview: getScreen('pet') });
    this.view.setFlipped(false);
  }

  protected async animateViewPosition(
    sceneView: View,
    destination: EntityPosition,
    duration: number,
  ) {
    if (sceneView !== this.view.getSuperview()) {
      destination.y += getScreenTop();
    }
    const { pet, care } = StateObserver.getState();

    const { id } = fromEntityId(this.view.entityId);
    const { action, statusEffects, species } = pet[id];
    const activeScript = this.pet.conversation.getActiveScript();

    const args = {
      id,
      action,
      species,
      ...statusEffects,
      ...pet[id].attributes,
      activeScript,
      currentCareAction: !!care.currentCareAction,
    };

    // Don't show sleep animation based on status effect
    // We use an action animation instead to ensure the pet only sleeps in bed
    args.asleep = false;

    if (args.species) {
      this.view.setAnimation(getPetAnimation(args, 'walking'));
    }

    await super.animateViewPosition(sceneView, destination, duration);

    if (args.species) {
      this.view.setAnimation(getPetAnimation(args, 'idle'));
    }
  }

  public async moveTo(position: Position | null, duration?: number) {
    const { entityId } = this.view;
    const { positions } = PetPositionController;

    // Update position record.
    if (position) {
      positions[entityId] = position;
    }
    // Make sure to delete null positions.
    else {
      delete positions[entityId];
    }

    // Execute movement.
    return super.moveTo(position, duration);
  }

  public async setOffset(x: number, y: number) {
    this.entity.style.offsetX = this.originalOffset.x + x;
    this.entity.style.offsetY = this.originalOffset.y + y;
  }
}
